import { type LANGUAGES } from '../../locale/Translation.consts'
import { type AlternateLink } from '../root/types'
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  DEFAULT_LANGUAGE,
  DEFAULT_COUNTRY,
  URL_PARAMS,
  SUPPORTED_COUNTRIES,
} from './consts'
import { type LocaleConfig } from './types'
import { type ProductRouteParams } from './productPages/types'

const LOCALE_PATH_REGEX = /^\/([a-z]{2}-[A-Z]{2})\//
const LOCALE_FORMAT_REGEX = /^[a-z]{2}-[A-Z]{2}$/

export function getDefaultLocale(): string {
  return DEFAULT_LOCALE
}

export function isSupportedLocale(locale: string): boolean {
  return SUPPORTED_LOCALES.includes(locale)
}

export function createLocale(
  language: string = DEFAULT_LANGUAGE,
  country: string = DEFAULT_COUNTRY
): string {
  return `${language}-${country}`
}

export function parseLocale(locale: string): LocaleConfig {
  const [language, country] = locale.split('-')
  return { language, country }
}

export function isValidLocaleFormat(locale: string): boolean {
  return LOCALE_FORMAT_REGEX.test(locale)
}

export function isValidLocale(locale: string): boolean {
  return isValidLocaleFormat(locale) && isSupportedLocale(locale)
}

export function extractLocaleFromPath(pathname: string): string | null {
  const match = pathname.match(LOCALE_PATH_REGEX)
  return match ? match[1] : null
}

export function getValidLocaleFromPathname(pathname: string): string | null {
  const pathSegments = pathname.split('/').filter(Boolean)
  const potentialLocale = pathSegments[0]
  return potentialLocale && isValidLocale(potentialLocale)
    ? potentialLocale
    : null
}

export function extractLocaleConfigFromPath(
  pathname: string
): LocaleConfig | null {
  const locale = extractLocaleFromPath(pathname)
  if (!locale) return null

  return parseLocale(locale)
}

export function isLocaleBasedRoute(pathname: string): boolean {
  return LOCALE_PATH_REGEX.test(pathname)
}

export function extractAndCreateLocale(
  params: ProductRouteParams,
  requestPathname?: string
): string {
  const localeConfig = extractLocaleConfig(params, requestPathname)
  return createLocale(localeConfig.language, localeConfig.country)
}

export function extractLocaleConfig(
  params: ProductRouteParams,
  requestPathname?: string
): LocaleConfig {
  let language = params[URL_PARAMS.LANGUAGE] || DEFAULT_LANGUAGE
  let country = params[URL_PARAMS.FORCE_COUNTRY] || DEFAULT_COUNTRY

  if (requestPathname) {
    const pathLocaleConfig = extractLocaleConfigFromPath(requestPathname)
    if (pathLocaleConfig) {
      language = pathLocaleConfig.language
      country = pathLocaleConfig.country
    }
  }

  if (!language || typeof language !== 'string') {
    throw new Error(`Invalid language: ${language}`)
  }
  if (!country || typeof country !== 'string') {
    throw new Error(`Invalid country: ${country}`)
  }

  return { language, country }
}

export function getCountryForLanguage(
  language: string,
  currentCountry?: string
): string {
  const supportedCountriesForLang = SUPPORTED_COUNTRIES[language as LANGUAGES]

  if (!supportedCountriesForLang) {
    return DEFAULT_COUNTRY
  }

  if (
    currentCountry &&
    supportedCountriesForLang.includes(currentCountry as never)
  ) {
    return currentCountry
  }

  return supportedCountriesForLang[0]
}

export function changeLocaleInPath(
  pathname: string,
  newLanguage: string
): string | null {
  const currentLocaleConfig = extractLocaleConfigFromPath(pathname)
  if (!currentLocaleConfig) {
    return null
  }

  const newCountry = getCountryForLanguage(
    newLanguage,
    currentLocaleConfig.country
  )
  const newLocale = createLocale(newLanguage, newCountry)

  return pathname.replace(LOCALE_PATH_REGEX, `/${newLocale}/`)
}

export function navigateToLocaleOrLanguageRoute(newLanguage: string): void {
  const url = new URL(window.location.href)
  const currentPathname = window.location.pathname

  if (isLocaleBasedRoute(currentPathname)) {
    // For locale-based routes, update the locale in the path
    const newPath = changeLocaleInPath(currentPathname, newLanguage)
    if (newPath) {
      url.pathname = newPath
    }
  } else {
    // For non-locale routes, use the language query param
    url.searchParams.set('language', newLanguage)
  }

  window.location.href = url.href
}

function getAllLocaleVariants(pathname: string): Array<{
  locale: string
  path: string
}> {
  return SUPPORTED_LOCALES.map((locale) => ({
    locale,
    path: pathname.replace(LOCALE_PATH_REGEX, `/${locale}/`),
  }))
}

// Generates hreflang alternate links for SEO internationalization
// Tells search engines about different language versions to prevent duplicate content penalties
// Should only be called for locale-based routes (e.g., "/en-US/phototiles")
//
// Example:
//   pathname: "/en-US/phototiles", origin: "https://www.mixtiles.com"
//   → returns links for all supported locales + x-default
export function getAlternateLocaleLinks(
  pathname: string,
  origin: string
): AlternateLink[] {
  const variants = getAllLocaleVariants(pathname)

  const links = variants.map(({ locale, path }) => ({
    tagName: 'link' as const,
    rel: 'alternate' as const,
    hrefLang: locale,
    href: `${origin}${path}`,
  }))

  // Add x-default for the default locale
  const defaultVariant = variants.find((v) => v.locale === DEFAULT_LOCALE)
  if (defaultVariant) {
    links.push({
      tagName: 'link' as const,
      rel: 'alternate' as const,
      hrefLang: 'x-default',
      href: `${origin}${defaultVariant.path}`,
    })
  }

  return links
}
